import React from 'react';
import PropTypes from 'prop-types';
import { Col, Row, Switch } from 'antd';
import { ExclamationCircleOutlined, SearchOutlined } from '@ant-design/icons';
import GoogleMapReact from 'google-map-react';
import { geocodeByAddress } from 'react-places-autocomplete';
import Marker from '../../Map/Marker';
import styles from '../styles.less';
import { LocationContext } from '../../../hoc/Location';
import { SettingsContext } from '../../../hoc/Settings';
import { LocalizationContext } from '../../../hoc/Localization';
import Button from '../../Button';
import Input from '../../Input';
import AddressComp from '../AddressComp';

const MapSelector = ({ value, setValue, setModal, setInitialLocation }) => {
  const {
    countries,
    location: { center: defaultCenter },
  } = React.useContext(LocationContext);
  const {
    domainSettings: { region },
  } = React.useContext(SettingsContext);
  const { t } = React.useContext(LocalizationContext);

  const [locationCreateMode, setLocationCreateMode] = React.useState(false);
  const [validations, setValidations] = React.useState({
    city: true,
    street: true,
    houseNumber: true,
  });

  let address = [];
  if (value) {
    address = [value.city, value.street];
    if (value.house_number) {
      address.push(value.house_number);
    }
  }

  const formatAddress = React.useCallback(result => {
    const latLng = {
      lat: result.geometry.location.lat(),
      lng: result.geometry.location.lng(),
    };

    const { address_components: addressComponents } = result;
    const { houseNumber, street, city, zipCode, country } = AddressComp(addressComponents);

    return {
      ...latLng,
      city,
      street,
      country,
      zip_code: zipCode,
      house_number: houseNumber,
    };
  }, []);

  const handleLocationSelect = React.useCallback(
    async data => {
      const fetchedLocation = formatAddress(data);

      if (countries.includes(fetchedLocation.country)) {
        setValue(fetchedLocation);
      }
    },
    [formatAddress, setValue],
  );

  const onMapPositionChange = React.useCallback(
    event => {
      // eslint-disable-next-line
      const {
        center: { lat, lng },
      } = event;
      setValidations({
        city: true,
        street: true,
        houseNumber: true,
      });

      if (!locationCreateMode && window.google) {
        // eslint-disable-next-line
        const geocoder = new google.maps.Geocoder();

        geocoder.geocode({ location: { lat, lng }, region }, res => {
          if (res && res.length) {
            handleLocationSelect(res[0]);
          }
        });
      } else {
        setValue({
          ...value,
          lat: parseFloat(lat),
          lng: parseFloat(lng),
        });
      }
    },
    [handleLocationSelect, locationCreateMode, value],
  );

  const handleLocationChange = React.useCallback(
    async data => {
      const fetchedAddresses = await geocodeByAddress(data);
      const result = fetchedAddresses[0];

      const fetchedLocation = formatAddress(result);

      setValue(fetchedLocation);
    },
    [formatAddress, setValue],
  );

  const validateFields = React.useCallback(() => {
    const validationStatuses = {
      street: true,
      houseNumber: true,
      city: true,
    };

    if (!value.street) {
      validationStatuses.street = false;
    }

    if (!value.house_number) {
      validationStatuses.houseNumber = false;
    }

    if (!value.city) {
      validationStatuses.city = false;
    }

    setValidations(validationStatuses);
    return validationStatuses.street && validationStatuses.houseNumber && validationStatuses.city;
  }, [value, setValidations]);

  const changeField = React.useCallback(
    (name, item) => {
      setValue({
        ...value,
        [name]: item,
      });
    },
    [value, setValue],
  );

  const { street, city, houseNumber } = validations;

  return (
    <div>
      <div className={styles.inputContainer}>
        <Row className={styles.row}>
          <Col className={styles.col} md={6} sm={24}>
            <div className={styles.validationStatus}>
              {!street && (
                <span className={styles.red}>{t('school.edit.location.street.validation')}</span>
              )}
              {!street && <ExclamationCircleOutlined className={styles.red} />}
            </div>
            <Input
              version="default"
              value={value && `${value.street}`}
              onChange={s => {
                changeField('street', s.target.value);
              }}
              placeholder={t('school.edit.location.street.placeholder')}
            />
          </Col>
          <Col className={styles.col} md={4} sm={24}>
            <Input
              version="short"
              value={value && `${value.zip_code}`}
              onChange={s => {
                changeField('zip_code', s.target.value);
              }}
              placeholder={t('school.edit.location.zipCode.placeholder')}
            />
          </Col>
          <Col className={styles.col} md={4} sm={24}>
            <div className={styles.validationStatus}>
              {!houseNumber && (
                <span className={styles.red}>
                  {t('school.edit.location.house_number.validation')}
                </span>
              )}
              {!houseNumber && <ExclamationCircleOutlined className={styles.red} />}
            </div>
            <Input
              version="short"
              style={{
                borderColor: (!houseNumber && '#f00') || '#D8D8D8',
              }}
              value={value && value.house_number}
              onChange={hn => {
                changeField('house_number', hn.target.value);
              }}
              placeholder={t('school.edit.location.house_number.placeholder')}
            />
          </Col>
          <Col className={styles.col} md={6} sm={24}>
            <div className={styles.validationStatus}>
              {!city && (
                <span className={styles.red}>{t('school.edit.location.city.validation')}</span>
              )}
              {!city && <ExclamationCircleOutlined className={styles.red} />}
            </div>
            <Input
              version="default"
              style={{
                borderColor: (!city && '#f00') || '#D8D8D8',
              }}
              value={value && value.city}
              onChange={c => {
                changeField('city', c.target.value);
              }}
              placeholder={t('school.edit.location.city.placeholder')}
            />
          </Col>
        </Row>
        <Button
          type="secondary"
          className={[styles.item, styles.find].join(' ')}
          onClick={() => {
            let { street: initialStreet } = value;

            if (!(initialStreet.search('Street') >= 0)) {
              initialStreet += ' Street';
            }

            handleLocationChange(
              `${value.city || ''} ${initialStreet || ''} ${value.house_number || ''}`,
            );
          }}
        >
          <SearchOutlined />
          {t('school.edit.location.find')}
        </Button>
        <Button
          type="primary"
          className={styles.item}
          onClick={() => {
            if (validateFields()) {
              setModal(false);
              setInitialLocation(value);
            }
          }}
        >
          {t('school.edit.location.confirm')}
        </Button>
      </div>
      <div className={styles.customizeContainer}>
        <span className={styles.customize}>{t('school.edit.location.customize')}</span>
        <Switch
          defaultChecked={false}
          size="small"
          checked={locationCreateMode}
          onChange={() => {
            setLocationCreateMode(!locationCreateMode);
          }}
        />
      </div>
      <div className={styles.map}>
        <div className={styles.mapPinter}>
          <Marker lat={parseFloat(value.lat)} lng={parseFloat(value.lng)} />
        </div>
        <GoogleMapReact
          bootstrapURLKeys={{
            key: process.env.GOOGLE_MAPS_API_KEY,
            language: 'en',
          }}
          defaultCenter={{
            lat: defaultCenter.lat,
            lng: defaultCenter.lng,
          }}
          defaultZoom={14}
          center={{
            lat: parseFloat(value.lat) || defaultCenter.lat,
            lng: parseFloat(value.lng) || defaultCenter.lng,
          }}
          onChange={onMapPositionChange}
        />
      </div>
    </div>
  );
};

MapSelector.propTypes = {
  value: PropTypes.object,
  setValue: PropTypes.func,
  setModal: PropTypes.func,
  setInitialLocation: PropTypes.func,
};

MapSelector.defaultProps = {
  value: {},
  setValue: () => { },
  setModal: () => { },
  setInitialLocation: () => { },
};

export default MapSelector;
